// ForgotPasswordReset.tsx

import {
  TextField,
  Button,
  Paper,
  Avatar,
  Snackbar,
  Divider,
  Box,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/escudo.png";
import Loader from "../../components/loader/loader";
import { useAuth } from "../../services/auth/AuthContext";
import { wait } from "@testing-library/user-event/dist/utils";
import { emailRegex } from "../../utils/regex";
import { LOGIN } from "../../utils/app-routes";
import PasswordField from "../../components/password-field/password-field";

const validateEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateName = (name: string) => /^[a-zA-ZÀ-ÿ\s'-]+$/.test(name);

const validatePassword = (password: string) => {
  const msgs: string[] = [];
  if (password.length < 6)
    msgs.push("La contraseña debe tener al menos 6 caracteres.");
  if (!/[a-z]/.test(password))
    msgs.push("Debe contener al menos una letra minúscula.");
  if (!/[A-Z]/.test(password))
    msgs.push("Debe contener al menos una letra mayúscula.");
  if (!/[0-9]/.test(password)) msgs.push("Debe contener al menos un número.");
  if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password))
    msgs.push("Debe contener al menos un carácter especial.");
  return msgs.length ? msgs.join(" ") : null;
};

const ForgotPassword: React.FC = () => {
  const { forgotPassword, confirmPassword } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    code: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [loadingRequestCode, setLoadingRequestCode] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotSteps, setForgotSteps] = useState(0);
  const [snackBarOpen, setSAnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.email) {
      setFormData((prevData) => ({
        ...prevData,
        email: location.state.email || "",
      }));
    }
  }, [location.state]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const requestCode = async () => {
    setSnackBarMessage(
      "El código ha sido enviado exitosamente al correo electrónico proporcionado. Por favor, revisa tu bandeja de entrada"
    );

    const newErrors: { [key: string]: string } = {};
    if (!formData.email) {
      newErrors.email = "El correo electrónico es obligatorio";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "El correo electrónico no es válido";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoadingRequestCode(true);
      const requestCode = await forgotPassword(formData.email);

      if (requestCode) {
        setLoadingRequestCode(false);
        setForgotSteps(1);
        setSAnackBarOpen(true);
      } else {
        setSnackBarMessage(
          "Lo sentimos, algo salió mal, inténtalo de nuevo más tarde."
        );
        setLoadingRequestCode(true);
        setSAnackBarOpen(true);
      }
    } catch {
      setSnackBarMessage(
        "Lo sentimos, algo salió mal, inténtalo de nuevo más tarde."
      );
      setLoadingRequestCode(true);
      setSAnackBarOpen(true);
    }
  };

  const waitTwoSeconds = async (): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, 2000));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validaciones
    const newErrors: { [key: string]: string } = {};
    if (!formData.email) {
      newErrors.email = "El correo electrónico es obligatorio";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "El correo electrónico no es válido";
    }
    if (!formData.code) {
      newErrors.code = "El código de verificación es obligatorio";
    }
    if (!formData.newPassword) {
      newErrors.newPassword = "La nueva contraseña es obligatoria";
    } else {
      const pwdErr = validatePassword(formData.newPassword);
      if (pwdErr) newErrors.newPassword = pwdErr;
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      newErrors.confirmNewPassword = "Las contraseñas no coinciden";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await confirmPassword(
        formData.email,
        formData.code,
        formData.newPassword
      );

      if (response) {
        setSnackBarMessage(
          "Tu contraseña ha sido cambiada correctamente. Ahora puedes iniciar sesión con tu nueva contraseña."
        );
        setSAnackBarOpen(true);

        (async () => {
          await waitTwoSeconds();
          navigate(LOGIN);
        })();
      } else {
        setSnackBarMessage(
          "Lo sentimos, la información proporcionada no es correcta, inténtalo de nuevo."
        );
        setLoadingRequestCode(false);
        setSAnackBarOpen(true);
      }
    } catch (error) {
      setSnackBarMessage(
        "Lo sentimos, algo salió mal, inténtalo de nuevo más tarde."
      );
      setLoadingRequestCode(false);
      setSAnackBarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const snackBar = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackBarOpen}
        onClose={() => setSAnackBarOpen(false)}
        message={snackBarMessage}
        autoHideDuration={2000}
        key="snackbar-forgot-password"
      />
    );
  };

  // const handlePassword = (value: string) => {
  //   setFormData({ ...formData, ["newPassword"]: value });
  //   setErrors({ ...errors, ["newPassword"]: "" });
  // };

  // const handleConfirmPassword = (value: string) => {
  //   setFormData({ ...formData, ["confirmNewPassword"]: value });
  //   setErrors({ ...errors, ["confirmNewPassword"]: "" });
  // };

  const handlePassword = (value: string) => {
    const pwdError = validatePassword(value); // ← valida en vivo
    setFormData((prev) => ({ ...prev, newPassword: value }));
    setErrors((prev) => ({
      ...prev,
      newPassword: pwdError ? pwdError : "",
      // si ya hay confirmación escrita, vuelves a validar coincidencia
      confirmNewPassword:
        formData.confirmNewPassword && value !== formData.confirmNewPassword
          ? "Las contraseñas no coinciden"
          : "",
    }));
  };

  const handleConfirmPassword = (value: string) => {
    setFormData((prev) => ({ ...prev, confirmNewPassword: value }));
    setErrors((prev) => ({
      ...prev,
      confirmNewPassword:
        formData.newPassword && value !== formData.newPassword
          ? "Las contraseñas no coinciden"
          : "",
    }));
  };

  return (
    <div
      className={`login_bg flex items-center	 justify-center w-full h-screen `}
    >
      <div className={`login_protector`}></div>
      <Paper className={`login_container flex flex-col  p-6`}>
        {/* <Avatar alt="Logo Esparza" src={logo} className={`logo_container`} /> */}

        <Box
          sx={{
            width: 150, // ajusta al diseño
            height: 150,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 2,
            bgcolor: "transparent",
          }}
          className="flex-shrink-0 self-center"
        >
          <img
            src={logo}
            alt="Logo Esparza"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
        {forgotSteps === 0 && (
          <div className="max-w-md mx-auto p-4 mt-5">
            <h2 className="mb-1 mt-5">
              <strong>Recuperar contraseña</strong>
            </h2>
            <p className="mb-3 mt-3">
              Por favor, ingresa tu dirección de correo electrónico para recibir
              un código de verificación y poder recuperar tu contraseña.
            </p>
            <TextField
              label="Correo Electrónico"
              name="email"
              type="email"
              className="mb-5"
              value={formData.email}
              onChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              fullWidth
              margin="normal"
              disabled={Boolean(location.state?.email)}
            />
            {loadingRequestCode && (
              <div className="w-full">
                <Loader />
              </div>
            )}
            <Button
              onClick={() => requestCode()}
              variant="contained"
              color="primary"
              className="mt-5 w-full"
              disabled={loadingRequestCode}
            >
              Restablecer Contraseña
            </Button>
            {errors.general && <p className="error">{errors.general}</p>}

            <Divider className="w-full margin">¿Ya tienes el código?</Divider>
            <Button onClick={() => setForgotSteps(1)} className="margin w-full">
              Ingresar código
            </Button>
          </div>
        )}

        {forgotSteps === 1 && (
          <form className="max-w-md mx-auto p-4" onSubmit={handleSubmit}>
            <h2 className="mb-1 mt-20">
              <strong>Recuperar contraseña</strong>
            </h2>
            <TextField
              label="Correo Electrónico"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              fullWidth
              margin="normal"
              disabled={Boolean(location.state?.email)}
            />
            <TextField
              label="Código de Verificación"
              name="code"
              value={formData.code}
              onChange={handleChange}
              error={Boolean(errors.code)}
              helperText={errors.code}
              fullWidth
              margin="normal"
            />

            <div className="w-full mb-3 mt-3">
              <PasswordField
                isConfirm
                name="newPassword"
                id="1"
                value={formData.newPassword}
                onChange={handlePassword}
                label="Contraseña"
                error={Boolean(errors.newPassword)}
                helperText={errors.newPassword}
              />
            </div>

            <div className="w-full mb-3 mt-3">
              <PasswordField
                isConfirm
                name="confirmNewPassword"
                id="2"
                value={formData.confirmNewPassword}
                onChange={handleConfirmPassword}
                label="Confirmar Nueva Contraseña"
                error={Boolean(errors.confirmNewPassword)}
                helperText={errors.confirmNewPassword}
              />
            </div>

            {/* <TextField
              label="Nueva Contraseña"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              error={Boolean(errors.newPassword)}
              helperText={errors.newPassword}
              fullWidth
              margin="normal"
            /> */}

            {/* 
            <TextField
              label="Confirmar Nueva Contraseña"
              name="confirmNewPassword"
              type="password"
              value={formData.confirmNewPassword}
              onChange={handleChange}
              error={Boolean(errors.confirmNewPassword)}
              helperText={errors.confirmNewPassword}
              fullWidth
              margin="normal"
            /> */}
            {errors.general && <p className="error">{errors.general}</p>}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="mt-4 w-full"
              disabled={isSubmitting}
            >
              Restablecer Contraseña
            </Button>
            <Divider className="w-full margin"></Divider>
            <Button className="margin w-full" href={LOGIN}>
              Iniciar sesión
            </Button>
          </form>
        )}
      </Paper>
      {snackBar()}
    </div>
  );
};

export default ForgotPassword;
